/**
 * Copyright (c) 2019, Tidepool Project
 *
 * This program is free software; you can redistribute it and/or modify it under
 * the terms of the associated License, which is identical to the BSD 2-Clause
 * License as published by the Open Source Initiative at opensource.org.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the License for more details.
 *
 * You should have received a copy of the License along with this program; if
 * not, you can obtain one from Tidepool Project at tidepool.org.
 */

import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Container,
  Typography,
  TextField,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel
} from '@material-ui/core';
import TimeValuePicker from './TimeValuePicker.js';
import SettingsCalc from '../../../views/SettingsCalc';
import CorrectionRange from './CorrectionRange.js';
import PatientInfoSnippet from './PatientInfoSnippet.js';
import HelpPopup from './HelpPopup.js';
import PatientSelfStart from './PatientSelfStart.js';
import * as _ from '../../../data/constants.js';

var utils = require('../../../Utils.js');

const useStyles = makeStyles(theme => ({
  box: {
    'background-color': '#f9f9f9',
    'margin-bottom': '24px',
    'margin-top': '24px',
    'margin-left': '12px',
    'margin-right': '12px',
    padding: '5px',
    display: 'flex',
    'align-items': 'flex-start'
  },
  titles: {
    'margin-left': '24px'
  },
  biggerTitles: {
    'margin-left': '12px',
    'margin-bottom': '24px',
    'margin-top': '12px'
  },
  border: {
    'border-radius': '10px',
    border: '2px solid #EDEDED',
    padding: '8px',
    margin: '24px'
  },
  aligned: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    'margin-left': 'auto',
    'margin-right': 'auto',
    'align-items': 'center'
  },
  page: {
    width: '80%',
    display: 'flex-box',
    flexDirection: 'column',
    'margin-left': 'auto',
    'margin-right': 'auto',
    'align-items': 'center'
  },
  innerElements: {
    'margin-left': '48px',
    'margin-right': '48px'
  },
  inline: {
    display: 'flex',
    flexDirection: 'row',
    'align-items': 'center'
  },
  helpIcon: {
    'margin-left': '12px'
  },
  selfStart: {
    'margin-left': '12px'
  }
}));

// Page 2 of the form, handles and collects all of the useful patient information regarding loop settings
const LoopSettings = props => {
  const classes = useStyles();

  // scroll to top on open
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <form onSubmit={props.handleSubmit}>
      <Container>
        <div className={classes.page}>
          <div>
            <div className={classes.border}>
              <PatientInfoSnippet
                patient={props.values.firstName + ' ' + props.values.lastName}
                dateOfBirth={props.values.birthday}
                email={props.values.email}
              />
            </div>
            <div className={classes.border}>
              <div className={classes.biggerTitles}>
                <div className={classes.inline}>
                  <div>
                    <Typography variant="subtitle1" color="textSecondary">
                      In-person training with a Tidepool Loop or DASH certified trainer
                    </Typography>
                  </div>
                  <div className={classes.helpIcon}>
                    <HelpPopup helpMessage={_.PatientSelfStartPopup} />
                  </div>
                </div>
              </div>
              <div className={classes.box}>
                <div className={classes.aligned}>
                  <FormControl>
                    <RadioGroup name="training" defaultValue={props.values.training} onChange={props.handleChange} row>
                      <FormControlLabel
                        value="in-person"
                        control={<Radio color="default" />}
                        label={<Typography color="textSecondary">Training Required</Typography>}
                      />
                      <FormControlLabel
                        value="in-module"
                        control={<Radio color="default" />}
                        label={<Typography color="textSecondary">Online Module start</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>
                </div>
              </div>
            </div>
            <div>
              {props.values.training === 'in-module' && <PatientSelfStart firstName={props.values.firstName} />}
              <div className={classes.border}>
                <div className={classes.biggerTitles}>
                  <Typography variant="h6" color="textSecondary">
                    Blood Glucose Settings
                  </Typography>
                </div>
                <div className={classes.innerElements}>
                  <div className={classes.inline}>
                    <div>
                      <Typography variant="subtitle1" color="textSecondary" className={classes.titles}>
                        Correction Range
                      </Typography>
                    </div>
                    <div className={classes.helpIcon}>
                      <HelpPopup helpMessage={_.CorrectionRangePopup} />
                    </div>
                  </div>
                  <div className={classes.box}>
                    <CorrectionRange
                      setFieldValue={props.setFieldValue}
                      values={props.values.correctionRange}
                      handleChange={props.handleChange}
                      fieldType="correctionRange"
                      errors={props.errors.correctionRange}
                      touched={props.touched.correctionRange}
                    />
                  </div>
                  <div className={classes.inline}>
                    <div>
                      <Typography variant="subtitle1" color="textSecondary" className={classes.titles}>
                        Suspend Threshold
                      </Typography>
                    </div>
                    <div className={classes.helpIcon}>
                      <HelpPopup helpMessage={_.SuspendThresholdPopup} />
                    </div>
                  </div>
                  <div className={classes.box}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      name="suspendThreshold"
                      type="number"
                      value={props.values.suspendThreshold}
                      onChange={props.handleChange}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">mg/dl</InputAdornment>,
                        style: { color: utils.thresholdWarnings(props.values.suspendThreshold).color }
                      }}
                      // eslint-disable-next-line react/jsx-no-duplicate-props
                      inputProps={utils.barriers('threshold')}
                      FormHelperTextProps={{
                        style: { color: utils.thresholdWarnings(props.values.suspendThreshold).color }
                      }}
                      error={props.touched.suspendThreshold && Boolean(props.errors.suspendThreshold)}
                      helperText={utils.helperTextPicker(
                        props.touched.suspendThreshold,
                        props.errors.suspendThreshold,
                        utils.thresholdWarnings(props.values.suspendThreshold)
                      )}
                    />
                  </div>
                </div>
              </div>
              <div className={classes.border}>
                <div className={classes.biggerTitles}>
                  <Typography variant="h6" color="textSecondary">
                    Insulin Settings
                  </Typography>
                </div>
                <div className={classes.innerElements}>
                  <div className={classes.inline}>
                    <div>
                      <Typography variant="subtitle1" color="textSecondary" className={classes.titles}>
                        Insulin Model
                      </Typography>
                    </div>
                    <div className={classes.helpIcon}>
                      <HelpPopup helpMessage={_.InsulinModelPopup} />
                    </div>
                  </div>
                  <div className={classes.box}>
                    <div className={classes.aligned}>
                      <Typography color="error" variant="subtitle1">
                        {props.touched.insulinModel ? props.errors.insulinModel : ''}
                      </Typography>
                      <RadioGroup
                        aria-label="position"
                        name="insulinModel"
                        value={props.values.insulinModel}
                        onChange={props.handleChange}
                        row
                      >
                        <FormControlLabel
                          value="rapidAdult"
                          control={<Radio color="default" />}
                          label={<Typography color="textSecondary">Rapid Acting Adult</Typography>}
                        />
                        <FormControlLabel
                          value="rapidChild"
                          control={<Radio color="default" />}
                          label={<Typography color="textSecondary">Rapid Acting Child</Typography>}
                        />
                      </RadioGroup>
                    </div>
                  </div>
                  <div className={classes.inline}>
                    <div>
                      <Typography variant="subtitle1" color="textSecondary" className={classes.titles}>
                        Max Basal
                      </Typography>
                    </div>
                    <div className={classes.helpIcon}>
                      <HelpPopup helpMessage={_.MaxBasalPopup} />
                    </div>
                  </div>
                  <div className={classes.box}>
                    {props.values.loopMode === 'clm' ? (
                      <TextField
                        fullWidth
                        variant="outlined"
                        name="maxBasal"
                        type="number"
                        value={props.values.maxBasal}
                        onChange={props.handleChange}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">U/hr</InputAdornment>,
                          style: { color: utils.deliveryLimitsWarnings(props.values.maxBasal, 'basal').color }
                        }}
                        // eslint-disable-next-line react/jsx-no-duplicate-props
                        inputProps={utils.barriers('maxBasal')}
                        FormHelperTextProps={{
                          style: { color: utils.deliveryLimitsWarnings(props.values.maxBasal, 'basal').color }
                        }}
                        error={props.touched.maxBasal && Boolean(props.errors.maxBasal)}
                        helperText={utils.helperTextPicker(
                          props.touched.maxBasal,
                          props.errors.maxBasal,
                          utils.deliveryLimitsWarnings(props.values.maxBasal, 'basal')
                        )}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        disabled
                        variant="outlined"
                        value="100%"
                        helperText="Suspend-only mode selected, max basal will be 100% of scheduled basal"
                      />
                    )}
                  </div>
                  <div className={classes.inline}>
                    <div>
                      <Typography variant="subtitle1" color="textSecondary" className={classes.titles}>
                        Max Bolus
                      </Typography>
                    </div>
                    <div className={classes.helpIcon}>
                      <HelpPopup helpMessage={_.MaxBolusPopup} />
                    </div>
                  </div>
                  <div className={classes.box}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      name="maxBolus"
                      type="number"
                      value={props.values.maxBolus}
                      onChange={props.handleChange}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">U</InputAdornment>,
                        style: { color: utils.deliveryLimitsWarnings(props.values.maxBolus, 'bolus').color }
                      }}
                      // eslint-disable-next-line react/jsx-no-duplicate-props
                      inputProps={utils.barriers('maxBolus')}
                      FormHelperTextProps={{
                        style: { color: utils.deliveryLimitsWarnings(props.values.maxBolus, 'bolus').color }
                      }}
                      error={props.touched.maxBolus && Boolean(props.errors.maxBolus)}
                      helperText={utils.helperTextPicker(
                        props.touched.maxBolus,
                        props.errors.maxBolus,
                        utils.deliveryLimitsWarnings(props.values.maxBolus, 'bolus')
                      )}
                    />
                  </div>
                  <div className={classes.inline}>
                    <div>
                      <Typography variant="subtitle1" color="textSecondary" className={classes.titles}>
                        Basal rates
                      </Typography>
                    </div>
                    <div className={classes.helpIcon}>
                      <HelpPopup helpMessage={_.ScheduledBasalRatesPopup} />
                    </div>
                  </div>
                  <div className={classes.box}>
                    <TimeValuePicker
                      setFieldValue={props.setFieldValue}
                      values={props.values.basalArray}
                      handleChange={props.handleChange}
                      tableType="basal"
                      fieldType="basalArray"
                      errors={props.errors.basalArray}
                      touched={props.touched.basalArray}
                    />
                  </div>
                  <div className={classes.inline}>
                    <div>
                      <Typography variant="subtitle1" color="textSecondary" className={classes.titles}>
                        Insulin to carb ratios
                      </Typography>
                    </div>
                    <div className={classes.helpIcon}>
                      <HelpPopup helpMessage={_.CarbToInsulinRatioPopup} />
                    </div>
                  </div>
                  <div className={classes.box}>
                    <TimeValuePicker
                      setFieldValue={props.setFieldValue}
                      values={props.values.bolusArray}
                      handleChange={props.handleChange}
                      tableType="bolus"
                      fieldType="bolusArray"
                      errors={props.errors.bolusArray}
                      touched={props.touched.bolusArray}
                    />
                  </div>
                  <div className={classes.inline}>
                    <div>
                      <Typography variant="subtitle1" color="textSecondary" className={classes.titles}>
                        Insulin sensitivity factors
                      </Typography>
                    </div>
                    <div className={classes.helpIcon}>
                      <HelpPopup helpMessage={_.InsulinSensitivityFactorPopup} />
                    </div>
                  </div>
                  <div className={classes.box}>
                    <TimeValuePicker
                      setFieldValue={props.setFieldValue}
                      values={props.values.sensitivityArray}
                      handleChange={props.handleChange}
                      tableType="sensitivity"
                      fieldType="sensitivityArray"
                      errors={props.errors.sensitivityArray}
                      touched={props.touched.sensitivityArray}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </form>
  );
};

export default LoopSettings;
